import React, { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@app/core/hooks/redux-hooks';
import styles from './expandall.module.scss';
import { FormProvider, useForm } from 'react-hook-form';
import { formConfigBase } from '@app/shared/constants/form-config-base';
import { useLocale } from '@app/core/hooks/useLocale';
import { Toggle, ToggleSize } from '../toggle/Toggle';
import {
  setAllFirstLastWaterfallExpandState,
  setExpandAllFirstLastWaterfallState,
} from '@app/core/store/ui-values-slice';
import { FirstLastExpandedSectionsState } from '@app/modules/projects/outputs/opm-summary-waterfall/first-last-5-erf/instrument-calculations-erf/InstrumentCalculationsErf';

const ExpandAllFirstLastWaterfall: React.FC = () => {
  const dispatch = useAppDispatch();
  const { l } = useLocale();
  const expandAll = useAppSelector((state) => state.uiValues.expandAllFirstLastWaterfall);
  const firstLastExpandedStates = useAppSelector(
    (state) => state.uiValues.userSelections.firstLastWaterfallTableExpandStates
  );

  const riskFreeResults = useAppSelector((state) => state.opmCalculation.riskFreeValues);
  const firstLastResults = riskFreeResults?.waterfalls;

  const forceChecked = useMemo(() => allTrue(firstLastExpandedStates), [firstLastExpandedStates]);

  function allTrue(firstLastExpandedStates: FirstLastExpandedSectionsState): boolean {
    if (!firstLastExpandedStates) {
      return false;
    }
    return (
      firstLastExpandedStates.valuation &&
      Object.values(firstLastExpandedStates.instruments).every(
        (instrument) =>
          instrument.expanded === true &&
          instrument.interimCashFlows === true &&
          instrument.exitDistributions === true
      )
    );
  }

  useEffect(() => {
    dispatch(setExpandAllFirstLastWaterfallState(forceChecked));
  }, [dispatch, forceChecked]);

  const handleChange = () => {
    const newExpandAllState = !expandAll;
    dispatch(setExpandAllFirstLastWaterfallState(newExpandAllState));
    dispatch(setAllFirstLastWaterfallExpandState(newExpandAllState));
  };

  const formMethods = useForm({
    ...formConfigBase,
  });

  if (!firstLastResults) {
    return null;
  }

  return (
    <FormProvider {...formMethods}>
      <Toggle
        name="expandAll"
        label={l('_ExpandAllRows')}
        toggleSize={ToggleSize.Medium}
        className={styles['expand-all']}
        forceChecked={forceChecked}
        onChange={handleChange}
        ignoreReadOnly
        data-testid="expand-all-toggle"
      />
    </FormProvider>
  );
};

export default ExpandAllFirstLastWaterfall;
